import React, { useEffect, useRef, useState } from 'react';
import {
  Box, TextField, Button, Typography,
  InputAdornment, CircularProgress, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';

export default function GeofenceMap({ onLocationSet, initialLat, initialLng, initialRadius }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [lat, setLat] = useState(initialLat || '');
  const [lng, setLng] = useState(initialLng || '');
  const [radius, setRadius] = useState(initialRadius || 150);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [locationSet, setLocationSet] = useState(false);

  useEffect(() => {
    if (mapInstanceRef.current) return;

    const L = window.L;
    if (!L) return;

    const defaultLat = initialLat || 9.0579;
    const defaultLng = initialLng || 7.4951;

    const map = L.map(mapRef.current).setView([defaultLat, defaultLng], 15);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // Custom green marker icon
    const greenIcon = L.divIcon({
      className: '',
      html: `<div style="
        width:32px;height:32px;
        background:#1a4d2e;
        border:3px solid white;
        border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);
        box-shadow:0 2px 8px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    });

    if (initialLat && initialLng) {
      const marker = L.marker([initialLat, initialLng], { icon: greenIcon, draggable: true }).addTo(map);
      markerRef.current = marker;
      const circle = L.circle([initialLat, initialLng], {
        radius: initialRadius || 150,
        color: '#1a4d2e',
        fillColor: '#2d7a4f',
        fillOpacity: 0.15,
        weight: 2,
      }).addTo(map);
      circleRef.current = circle;
      setLocationSet(true);

      marker.on('dragend', (e) => {
        const pos = e.target.getLatLng();
        updateLocation(pos.lat, pos.lng, map);
      });
    }

    map.on('click', (e) => {
      placeMarker(e.latlng.lat, e.latlng.lng, map);
    });

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  const placeMarker = (newLat, newLng, map) => {
    const L = window.L;
    const mapInst = map || mapInstanceRef.current;
    if (!mapInst) return;

    const greenIcon = L.divIcon({
      className: '',
      html: `<div style="
        width:32px;height:32px;
        background:#1a4d2e;
        border:3px solid white;
        border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);
        box-shadow:0 2px 8px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    });

    if (markerRef.current) {
      markerRef.current.remove();
    }
    if (circleRef.current) {
      circleRef.current.remove();
    }

    const marker = L.marker([newLat, newLng], { icon: greenIcon, draggable: true }).addTo(mapInst);
    markerRef.current = marker;

    const currentRadius = parseInt(radius) || 150;
    const circle = L.circle([newLat, newLng], {
      radius: currentRadius,
      color: '#1a4d2e',
      fillColor: '#2d7a4f',
      fillOpacity: 0.15,
      weight: 2,
    }).addTo(mapInst);
    circleRef.current = circle;

    marker.on('dragend', (e) => {
      const pos = e.target.getLatLng();
      updateLocation(pos.lat, pos.lng, mapInst);
    });

    updateLocation(newLat, newLng, mapInst);
    setLocationSet(true);
  };

  const updateLocation = (newLat, newLng, mapInst) => {
    const roundedLat = Math.round(newLat * 1000000) / 1000000;
    const roundedLng = Math.round(newLng * 1000000) / 1000000;
    setLat(roundedLat);
    setLng(roundedLng);
    if (circleRef.current) {
      circleRef.current.setLatLng([newLat, newLng]);
    }
    if (onLocationSet) {
      onLocationSet({ lat: roundedLat, lng: roundedLng });
    }
  };

  const updateRadius = (newRadius) => {
    setRadius(newRadius);
    if (circleRef.current && newRadius > 0) {
      circleRef.current.setRadius(parseInt(newRadius));
    }
    if (onLocationSet && lat && lng) {
      onLocationSet({ lat: parseFloat(lat), lng: parseFloat(lng), radius: parseInt(newRadius) });
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchError('');
    try {
      const encoded = encodeURIComponent(searchQuery);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&limit=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      if (data.length === 0) {
        setSearchError('Location not found. Try a more specific search.');
        return;
      }
      const result = data[0];
      const newLat = parseFloat(result.lat);
      const newLng = parseFloat(result.lon);
      mapInstanceRef.current.setView([newLat, newLng], 16);
      placeMarker(newLat, newLng);
    } catch {
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const newLat = pos.coords.latitude;
      const newLng = pos.coords.longitude;
      mapInstanceRef.current.setView([newLat, newLng], 17);
      placeMarker(newLat, newLng);
    });
  };

  return (
    <Box>
      {/* Search bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField
          placeholder="Search for your office address..."
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#aaa' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={searchLoading}
          sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, minWidth: 90, borderRadius: 2 }}
        >
          {searchLoading ? <CircularProgress size={16} color="inherit" /> : 'Search'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleUseMyLocation}
          startIcon={<MyLocationIcon sx={{ fontSize: 16 }} />}
          sx={{ borderColor: '#2d7a4f', color: '#1a4d2e', borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          My location
        </Button>
      </Box>

      {searchError && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: 13, borderRadius: 2 }}>
          {searchError}
        </Alert>
      )}

      <Typography sx={{ fontSize: 12, color: '#888', mb: 1 }}>
        Click anywhere on the map to place the geofence marker, or drag the marker to adjust.
      </Typography>

      {/* Map container */}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: 380,
          borderRadius: 2,
          border: '0.5px solid #e0e8e0',
          overflow: 'hidden',
          zIndex: 0,
        }}
      />

      {/* Coordinates + radius inputs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1.5 }}>
        <TextField
          label="Latitude"
          size="small"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 16, color: '#aaa' }} /></InputAdornment>
          }}
        />
        <TextField
          label="Longitude"
          size="small"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 16, color: '#aaa' }} /></InputAdornment>
          }}
        />
        <TextField
          label="Radius (metres)"
          size="small"
          type="number"
          value={radius}
          onChange={(e) => updateRadius(e.target.value)}
        />
      </Box>

      {locationSet && (
        <Alert severity="success" sx={{ mt: 1.5, fontSize: 13, borderRadius: 2 }}>
          Location set — {lat}, {lng} with {radius}m radius
        </Alert>
      )}
    </Box>
  );
}